import type { FC } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { GameLayout, type IPlayer } from '@skillarea/game-layout';
import type { IGameState, TPlayer } from '@skillarea/game-fourinarow-phaser';

import { usePhaserGameLoader } from '../use-phaser-game-loader';
import type { TGamePlayer } from '../types';

import styles from './FourInARowGame.module.scss';

import { playGameStartSound } from '~/lib/game-sounds';
import { GameLoadingBlock } from '~/components/Matchmaking/screens/GameScreen';
import { useGameLayoutLabels } from '~/hooks/useGameLayoutLabels';
import { useLocalizedGameName } from '~/hooks/useLocalizedGame';

export interface IFourInARowGameResult {
  winner: TPlayer | null;
  isDraw: boolean;
}

export interface IFourInARowGameProps {
  player1: TGamePlayer;
  player2: TGamePlayer;
  board: (TPlayer | null)[][];
  currentPlayer: TPlayer;
  player1TimeLeftMs: number;
  player2TimeLeftMs: number;
  turnDurationMs: number;
  isGameOver: boolean;
  winningLine?: { row: number; col: number }[];
  winner?: TPlayer | null;
  isDraw?: boolean;
  playerSymbol?: TPlayer;
  isMyTurn?: boolean;
  onColumnClick?: (col: number) => void;
  onGameEnd?: (result: IFourInARowGameResult) => void;
  onSurrender?: () => void;
  rulesContent?: string;
}

function player01ToSymbol(player: TPlayer): 'X' | 'O' {
  return player === 0 ? 'X' : 'O';
}

export const FourInARowGame: FC<IFourInARowGameProps> = ({
  board,
  currentPlayer,
  isDraw = false,
  isGameOver,
  isMyTurn = false,
  onColumnClick,
  onGameEnd,
  onSurrender,
  player1: player1Info,
  player1TimeLeftMs,
  player2: player2Info,
  player2TimeLeftMs,
  playerSymbol = 0,
  rulesContent = '',
  turnDurationMs,
  winner,
  winningLine,
}) => {
  const gameLayoutLabels = useGameLayoutLabels();
  const gameName = useLocalizedGameName('four-in-a-row', 'Four in a Row');
  const containerRef = useRef<globalThis.HTMLDivElement>(null);

  type TFourInARowPhaserGame = {
    updateState: (state: IGameState) => void;
    setInteractive: (isInteractive: boolean) => void;
    destroy: () => void;
  };
  type TFourInARowPhaserGameConstructor = new (args: {
    parent: globalThis.HTMLDivElement;
    localPlayer: TPlayer;
    onColumnClick: (col: number) => void;
    onGameEnd: (winner: TPlayer | null, isDraw: boolean) => void;
  }) => TFourInARowPhaserGame;

  const gameRef = useRef<TFourInARowPhaserGame | null>(null);
  const onColumnClickRef = useRef(onColumnClick);
  const onGameEndRef = useRef(onGameEnd);
  const playerSymbolRef = useRef(playerSymbol);
  onColumnClickRef.current = onColumnClick;
  onGameEndRef.current = onGameEnd;
  playerSymbolRef.current = playerSymbol;

  const loadGame = useCallback(
    async (
      parent: globalThis.HTMLDivElement,
    ): Promise<TFourInARowPhaserGame> => {
      const mod = await import('@skillarea/game-fourinarow-phaser');
      const Game =
        mod.FourInARowPhaserGame as unknown as TFourInARowPhaserGameConstructor;
      const game = new Game({
        parent,
        localPlayer: playerSymbolRef.current,
        onColumnClick: (col) => {
          onColumnClickRef.current?.(col);
        },
        onGameEnd: (w, d) => {
          onGameEndRef.current?.({ winner: w, isDraw: d });
        },
      });
      const withReady = game as { whenReady?: () => Promise<void> };
      if (withReady.whenReady) await withReady.whenReady();
      return game;
    },
    [],
  );

  const player1: IPlayer = {
    id: 'player1',
    name: player1Info.name,
    avatarUrl: player1Info.avatarUrl,
    symbol: player01ToSymbol(playerSymbol),
    symbolContent: (
      <span
        className={
          playerSymbol === 0 ? styles.playerSymbolX : styles.playerSymbolO
        }
      />
    ),
  };

  const player2: IPlayer = {
    id: 'player2',
    name: player2Info.name,
    avatarUrl: player2Info.avatarUrl,
    symbol: playerSymbol === 0 ? 'O' : 'X',
    symbolContent: (
      <span
        className={
          playerSymbol === 0 ? styles.playerSymbolO : styles.playerSymbolX
        }
      />
    ),
  };

  const activePlayerId = isGameOver
    ? null
    : currentPlayer === playerSymbol
      ? 'player1'
      : 'player2';

  const { isReady } = usePhaserGameLoader({
    containerRef,
    gameRef,
    loadGame,
    onLoaded: playGameStartSound,
  });

  useEffect(() => {
    if (gameRef.current) {
      const gameState: IGameState = {
        board,
        isGameOver,
        winningLine,
        winner,
        isDraw,
      };
      gameRef.current.updateState(gameState);
      gameRef.current.setInteractive(isMyTurn && !isGameOver);
    }
  }, [board, isGameOver, winningLine, winner, isDraw, isMyTurn]);

  const gameContent = (
    <div className={styles.gameWrapper}>
      {!isReady ? <GameLoadingBlock className={styles.gameLoading} /> : null}
      <div className={styles.gameContainer} ref={containerRef} />
    </div>
  );

  return (
    <GameLayout
      activePlayerId={activePlayerId}
      gameColumnClassName={styles.gameColumnFourInARow}
      gameContent={gameContent}
      gameName={gameName}
      labels={gameLayoutLabels}
      onSurrender={onSurrender}
      player1={player1}
      player1TimeLeftMs={player1TimeLeftMs}
      player2={player2}
      player2TimeLeftMs={player2TimeLeftMs}
      rulesContent={rulesContent}
      turnDurationMs={turnDurationMs}
    />
  );
};
